import { Injectable, inject } from '@angular/core';
import { Router, NavigationExtras } from '@angular/router';
import { App, type URLOpenListenerEvent } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { environment } from 'src/environments/environment';

/** Key for storing pending deep link path when user is redirected to login */
export const PENDING_DEEP_LINK_PATH_KEY = 'pendingDeepLinkPath';

/** Default path when deep link is invalid or root */
const DEFAULT_PATH = '/';

/**
 * Deep link service: Custom URL Scheme + Universal / App Links.
 * - Custom scheme: selfwish://open/activity/my-slug (push, QR, ads, email)
 * - Universal links: https://theselfwish.com/activity/my-slug (browser, WhatsApp, email)
 * Handles cold start, background open, query params, and auth redirect (pending link).
 */
@Injectable({
  providedIn: 'root'
})
export class DeepLinkService {
  private router = inject(Router);

  /** Host for universal links (e.g. theselfwish.com). From environment.siteUrl. */
  private get linkHost(): string {
    try {
      return new URL(environment.siteUrl || 'https://theselfwish.com').hostname;
    } catch {
      return 'theselfwish.com';
    }
  }

  /**
   * Stripe and marketing often use www or subdomains; strict equality to linkHost
   * would drop `/trainer/stripe-connect/return` to `/` (home).
   */
  private isAllowedUniversalLinkHost(hostname: string): boolean {
    if (hostname === this.linkHost) return true;
    if (hostname === 'www.theselfwish.com' || hostname === 'theselfwish.com') return true;
    if (hostname === 'web-stage.theselfwish.com') return true;
    if (hostname.endsWith('.theselfwish.com')) return true;
    return false;
  }

  /**
   * Initialize deep linking. Call once from AppComponent (e.g. in ngOnInit).
   * Registers appUrlOpen listener and handles cold-start URL.
   */
  init(): void {
    if (!Capacitor.isNativePlatform()) return;
    this.addAppUrlOpenListener();
    this.handleInitialUrl();
  }

  /**
   * Listen for deep links when app is already open or coming from background.
   */
  addAppUrlOpenListener(): void {
    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      const url = event?.url;
      if (url) this.handleUrl(url);
    });
  }

  /**
   * Handle URL that launched the app (cold start). Safe to call when no launch URL.
   */
  async handleInitialUrl(): Promise<void> {
    try {
      const result = await App.getLaunchUrl();
      if (result?.url) this.handleUrl(result.url);
    } catch {
      // getLaunchUrl rejects when there was no launch URL
    }
  }

  /**
   * Parse URL and navigate. Supports:
   * - selfwish://open/activity/my-slug
   * - https://theselfwish.com/activity/my-slug
   * - Query params: ?ref=share&user=12
   */
  handleUrl(url: string): void {
    const { path, queryParams } = this.parseUrl(url);
    if (!path || path === '/') {
      this.router.navigateByUrl(DEFAULT_PATH);
      return;
    }
    const extras: NavigationExtras = Object.keys(queryParams).length
      ? { queryParams }
      : {};
    this.router.navigateByUrl(path, extras).catch(() => {
      this.router.navigateByUrl(DEFAULT_PATH);
    });
  }

  /**
   * Parse deep link URL into Angular path and query params.
   */
  parseUrl(url: string): { path: string; queryParams: Record<string, string> } {
    const queryParams: Record<string, string> = {};
    let path = DEFAULT_PATH;

    try {
      const parsed = new URL(url);

      if (parsed.protocol === 'selfwish:') {
        // selfwish://trainer/stripe-connect/refresh → hostname "trainer", pathname "/stripe-connect/refresh"
        if (parsed.hostname) {
          path = `/${parsed.hostname}${parsed.pathname || ''}`.replace(/\/{2,}/g, '/');
        } else {
          path = parsed.pathname || DEFAULT_PATH;
        }
      } else if (
        this.isAllowedUniversalLinkHost(parsed.hostname) &&
        (parsed.protocol === 'https:' || parsed.protocol === 'http:')
      ) {
        path = parsed.pathname || DEFAULT_PATH;
      } else {
        return { path: DEFAULT_PATH, queryParams };
      }

      parsed.searchParams.forEach((value, key) => {
        queryParams[key] = value;
      });

      path = path.replace(/^\/+/, '/').replace(/\/+$/, '') || '/';
    } catch {
      path = DEFAULT_PATH;
    }

    return { path, queryParams };
  }

  /**
   * Save the intended path when redirecting to login (e.g. from auth guard).
   * After login, call resolvePendingDeepLink() to navigate to this path.
   */
  setPendingDeepLinkPath(path: string): void {
    if (path && path !== '/' && !path.startsWith('/signin')) {
      localStorage.setItem(PENDING_DEEP_LINK_PATH_KEY, path);
    }
  }

  /**
   * Navigate to pending deep link path (if any) and clear it.
   * Call after successful login. Returns true if a pending path was resolved.
   */
  resolvePendingDeepLink(): boolean {
    const pending = localStorage.getItem(PENDING_DEEP_LINK_PATH_KEY);
    if (!pending) return false;
    localStorage.removeItem(PENDING_DEEP_LINK_PATH_KEY);
    this.router.navigateByUrl(pending).catch(() => {});
    return true;
  }

  /** Check if there is a pending deep link path. */
  hasPendingDeepLink(): boolean {
    return !!localStorage.getItem(PENDING_DEEP_LINK_PATH_KEY);
  }
}
